import { useState } from 'react';
import { useAppSelector } from '../../hooks/redux';
import { selectCompanies } from '../../store/slices/page-config.slice';
import { AppProject } from '../../models/page-config.models';
import { KWELLO_ICON, KwelloSvgIcon } from '../icon/KwelloSvgIcon';
import ClickableDiv from '../clickableDiv/ClickableDiv';
import { useResize } from '../../hooks/resize';
import { useCompareUrlWithProjectId } from 'common/hooks/route';
import { PROFILE_CAPTURE_ROUTES } from 'routes/routes.const';
import { useModuleProjectContext } from 'common/contexts/useModuleProjectContext';

function ProjectNameDisplay({
  project,
  onProjectChange,
}: Readonly<{
  project: AppProject;
  onProjectChange: (project: AppProject) => void;
}>) {
  const { activeProject } = useModuleProjectContext();

  return (
    <ClickableDiv
      className={
        project.projectDisplayName === activeProject?.projectDisplayName
          ? 'active-project'
          : 'project-display-name'
      }
      onClick={() => onProjectChange(project)}
    >
      {project.projectDisplayName}
    </ClickableDiv>
  );
}

export function ProjectSelectorContent({ onChange }: Readonly<{ onChange?: () => void }> = {}) {
  const { isMobile } = useResize();
  const showCompany = localStorage.getItem('showCompanyName') ?? 'false';
  const [showCompanyName, setShowCompanyName] = useState(showCompany === 'true');
  const { compareUrl } = useCompareUrlWithProjectId();
  const isProfileCapture = compareUrl(PROFILE_CAPTURE_ROUTES.root, { includes: true });
  const companies = useAppSelector(selectCompanies);
  const { activeModule, captureProjects, changeProject } = useModuleProjectContext();

  const projects: AppProject[] =
    !isMobile && !isProfileCapture ? activeModule?.projects ?? [] : captureProjects;
  const filteredCompanies = companies.filter((company) =>
    projects.find((project) => project.projectClientName === company)
  );
  const sortedProjects = projects
    ?.slice()
    .sort((a, b) => a.projectDisplayName.localeCompare(b.projectDisplayName));

  const onProjectChange = (project: AppProject) => {
    changeProject(project.moduleProjectID);

    onChange?.();
  };

  const onCheckboxSelect = () => {
    setShowCompanyName(!showCompanyName);
    const showCompany = showCompanyName ? 'false' : 'true';
    localStorage.setItem('showCompanyName', showCompany);
  };

  if (!projects.length) {
    return null;
  }

  return (
    <div className="projects-popup-content">
      {filteredCompanies.length > 1 && (
        <ClickableDiv
          className={`${showCompanyName ? 'project-select-checkbox' : 'project-select-checkbox-empty'}`}
          onClick={onCheckboxSelect}
        >
          <KwelloSvgIcon
            icon={showCompanyName ? KWELLO_ICON.CHECKED_BOX : KWELLO_ICON.UNCHECKED_BOX}
          />
          Show Company Name
        </ClickableDiv>
      )}
      {showCompanyName ? (
        <div className="scroll-content-company">
          {filteredCompanies.map((company: string) => {
            return (
              <div key={company} className="company-section">
                <div className="company-name">{company}</div>
                {sortedProjects
                  .filter((project) => project.projectClientName === company)
                  .map((companyProject) => (
                    <ProjectNameDisplay
                      onProjectChange={onProjectChange}
                      project={companyProject}
                      key={companyProject.moduleProjectID}
                    />
                  ))}
              </div>
            );
          })}
        </div>
      ) : (
        <div className="scroll-content">
          {sortedProjects.map((project) => (
            <ProjectNameDisplay
              onProjectChange={onProjectChange}
              project={project}
              key={project.moduleProjectID}
            />
          ))}
        </div>
      )}
    </div>
  );
}
